import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { CloseOutlined, LocationOffOutlined, OpenInFull } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  InfoWindow,
  Map,
  Marker,
  useMapsLibrary,
  useMarkerRef,
} from "@vis.gl/react-google-maps";
import { setCoordinates } from "../features/assessorSlice";
import { useMapsKey } from "./mapsProvider";

// The reserved map card's height — kept in one place so the placeholder and
// the map itself always agree, which is what stops the layout shift.
const MAP_CARD_HEIGHT = 220;

// Short enough that selecting a property resolves the map quickly (the old
// 1.2s wait was the "takes a second" lag before it appeared), long enough to
// still coalesce keystrokes when an address is typed by hand.
const GEOCODE_DEBOUNCE_MS = 400;

const isUsableAddress = (address) =>
  Boolean(address && address.trim() && address.trim() !== "NOT FOUND");

const isUsableText = (text) =>
  Boolean(text && String(text).trim() && String(text).trim() !== "NOT FOUND");

// Fills the reserved map card while there's no map to show — either coordinates
// are still being resolved ("locating") or the address can't be mapped
// ("none"). Occupies the card's full fixed size so nothing below it shifts.
const MapPlaceholder = ({ state }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{
      height: "100%",
      width: "100%",
      bgcolor: "action.hover",
      color: "text.secondary",
    }}
  >
    {state === "locating" ? (
      <>
        <CircularProgress size={22} />
        <Typography variant="caption">Locating…</Typography>
      </>
    ) : (
      <>
        <LocationOffOutlined fontSize="small" />
        <Typography variant="caption">No location available</Typography>
      </>
    )}
  </Stack>
);

// Marker with its info card already open, so the map never appears as a bare
// pin with no context. Lives inside the keyed <Map>, so switching properties
// remounts it and the card reopens; closing it leaves the marker clickable.
const MarkerWithCard = ({ position, address, ownerName, parcelID }) => {
  const [markerRef, marker] = useMarkerRef();
  const [cardOpen, setCardOpen] = useState(true);

  const headline = isUsableAddress(address) ? address.trim() : "Property location";
  const details = [
    isUsableText(ownerName) ? ownerName.trim() : null,
    isUsableText(parcelID) ? `Parcel ${String(parcelID).trim()}` : null,
  ].filter(Boolean);

  return (
    <>
      <Marker
        ref={markerRef}
        position={position}
        onClick={() => setCardOpen(true)}
      />
      {cardOpen && marker ? (
        <InfoWindow
          anchor={marker}
          maxWidth={260}
          shouldFocus={false}
          onCloseClick={() => setCardOpen(false)}
          headerContent={
            <Typography variant="subtitle2" sx={{ pr: 1 }}>
              {headline}
            </Typography>
          }
        >
          {details.map((line) => (
            <Typography key={line} variant="body2" color="text.secondary">
              {line}
            </Typography>
          ))}
        </InfoWindow>
      ) : null}
    </>
  );
};

// Small map of the property's location, expandable to a dialog. Uses stored
// latitude/longitude when present; otherwise geocodes the building address
// once and stores the result in the form (persisted on the next save).
const PropertyMapInner = () => {
  const dispatch = useDispatch();
  const geocoding = useMapsLibrary("geocoding");
  const latitude = useSelector((state) => state.assessor.value.form.latitude);
  const longitude = useSelector((state) => state.assessor.value.form.longitude);
  const buildingAddress = useSelector(
    (state) => state.assessor.value.form.buildingAddress
  );
  const ownerName = useSelector((state) => state.assessor.value.form.ownerName);
  const parcelID = useSelector((state) => state.assessor.value.form.parcelID);
  const [expanded, setExpanded] = useState(false);
  // The address whose geocode came back empty. Stored (not a boolean) so it
  // self-clears when a different property loads — geocodeFailed below compares
  // it to the current address, so a new address is never stale-"failed".
  const [failedAddress, setFailedAddress] = useState(null);
  const lastGeocoded = useRef(null);

  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);

  useEffect(() => {
    if (!geocoding || hasCoords || !isUsableAddress(buildingAddress)) {
      return;
    }
    if (lastGeocoded.current === buildingAddress) {
      return; // already tried this address — don't burn quota retrying
    }

    // Debounced so typing an address by hand doesn't geocode every keystroke.
    const timer = setTimeout(() => {
      lastGeocoded.current = buildingAddress;
      const geocoder = new geocoding.Geocoder();
      geocoder
        .geocode({ address: buildingAddress })
        .then((response) => {
          const location = response?.results?.[0]?.geometry?.location;
          if (location) {
            dispatch(
              setCoordinates({ latitude: location.lat(), longitude: location.lng() })
            );
          } else {
            // No match — show the "no location" state instead of a spinner.
            setFailedAddress(buildingAddress);
          }
        })
        .catch(() => {
          setFailedAddress(buildingAddress);
        });
    }, GEOCODE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [geocoding, hasCoords, buildingAddress, dispatch]);

  // The expand dialog only exists while there's a map; if coordinates go away
  // (property with no location, or the address is being re-typed) drop the
  // open flag so it can't silently reopen when a mapped property loads next.
  useEffect(() => {
    if (!hasCoords && expanded) {
      setExpanded(false);
    }
  }, [hasCoords, expanded]);

  const usableAddress = isUsableAddress(buildingAddress);
  // Only "failed" for the address currently in the form — a freshly loaded
  // property with a different address reads as still-locating, not failed.
  const geocodeFailed = usableAddress && failedAddress === buildingAddress;

  const position = hasCoords ? { lat: latitude, lng: longitude } : null;
  // Key remounts the map when the property moves, so defaultCenter re-applies
  // while leaving pan/zoom free in between (a controlled `center` would snap
  // back on every render).
  const mapKey = hasCoords ? `${latitude},${longitude}` : "no-map";
  const mapProps = {
    defaultCenter: position,
    defaultZoom: 17,
    gestureHandling: "cooperative",
    disableDefaultUI: false,
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* Fixed-height card is always present, so selecting a property never
          reflows the fields below it — the map (or its loading/empty state)
          fills into this reserved space instead of popping in a beat later. */}
      <Box
        sx={{
          height: MAP_CARD_HEIGHT,
          width: "100%",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {hasCoords ? (
          <Map key={mapKey} {...mapProps}>
            <MarkerWithCard
              position={position}
              address={buildingAddress}
              ownerName={ownerName}
              parcelID={parcelID}
            />
          </Map>
        ) : (
          <MapPlaceholder state={geocodeFailed ? "none" : "locating"} />
        )}
      </Box>
      {hasCoords ? (
        <Tooltip title="Expand map">
          <IconButton
            size="small"
            onClick={() => setExpanded(true)}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "#eee" },
            }}
          >
            <OpenInFull fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}

      {hasCoords ? (
        <Dialog
          fullWidth
          maxWidth="lg"
          open={expanded}
          onClose={() => setExpanded(false)}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography component="span" variant="h6">
              {buildingAddress || "Property Location"}
            </Typography>
            <IconButton onClick={() => setExpanded(false)}>
              <CloseOutlined />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ height: "70vh", p: 0 }}>
            <Map key={`dialog-${mapKey}`} {...mapProps}>
              <MarkerWithCard
                position={position}
                address={buildingAddress}
                ownerName={ownerName}
                parcelID={parcelID}
              />
            </Map>
          </DialogContent>
        </Dialog>
      ) : null}
    </Box>
  );
};

const PropertyMap = () => {
  const mapsKey = useMapsKey();
  if (!mapsKey) {
    return null;
  }
  return <PropertyMapInner />;
};

export default PropertyMap;
